import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Package, Edit, Save, X, RefreshCw } from "lucide-react";
import { PropertyType } from "@/api.types.v2";
import {
  useGetPropertySurcharges,
  useUpsertPropertySurcharge,
  useInitializeDefaultSurcharges,
  formatCurrency,
  getPropertyTypeLabel,
  type PropertySurcharge,
} from "@/api/property-surcharges";
import {
  useGetCarrierServices,
  type CarrierServiceListItem,
} from "@/api/carrier-services";

/**
 * Property surcharge admin — scope is "Global (all services)" or a specific
 * CarrierService. The table shows one row per PropertyType for the chosen
 * scope.
 */
const GLOBAL_SCOPE = "__global__";

type Scope =
  | { kind: "global" }
  | { kind: "carrierService"; carrierServiceId: string };

interface EditingState {
  surchargeCents: number;
  active: boolean;
}

export function PropertySurchargeForm() {
  const [scope, setScope] = useState<Scope>({ kind: "global" });
  const [editing, setEditing] = useState<Record<PropertyType, EditingState>>(
    {} as Record<PropertyType, EditingState>
  );

  const carrierServiceId =
    scope.kind === "carrierService" ? scope.carrierServiceId : undefined;

  const {
    data: surcharges,
    isLoading: surchargesLoading,
    refetch,
  } = useGetPropertySurcharges({ carrierServiceId });

  const { data: carrierServices, isLoading: servicesLoading } =
    useGetCarrierServices({ active: true });

  const upsertMutation = useUpsertPropertySurcharge({
    onSuccess: () => {
      toast.success("Saved", { description: "Property surcharge updated" });
      void refetch();
    },
    onError: (e: Error) => {
      toast.error("Save failed", { description: e.message });
    },
  });

  const initializeMutation = useInitializeDefaultSurcharges({
    onSuccess: () => {
      toast.success("Initialized", {
        description: "Default surcharges seeded",
      });
      void refetch();
    },
    onError: (e: Error) => {
      toast.error("Init failed", { description: e.message });
    },
  });

  const visibleSurcharges = useMemo(() => surcharges ?? [], [surcharges]);

  const surchargeByPropertyType = useMemo(() => {
    const map: Partial<Record<PropertyType, PropertySurcharge>> = {};
    for (const s of visibleSurcharges) map[s.propertyType] = s;
    return map;
  }, [visibleSurcharges]);

  // Group services by parent carrier for the picker
  const carrierGroups = useMemo(() => {
    if (!carrierServices)
      return [] as {
        carrierName: string;
        services: CarrierServiceListItem[];
      }[];
    const map: Record<string, CarrierServiceListItem[]> = {};
    for (const cs of carrierServices) {
      (map[cs.carrier.name] ??= []).push(cs);
    }
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([carrierName, services]) => ({ carrierName, services }));
  }, [carrierServices]);

  const handleScopeChange = (value: string) => {
    if (value === GLOBAL_SCOPE) {
      setScope({ kind: "global" });
    } else {
      setScope({ kind: "carrierService", carrierServiceId: value });
    }
    setEditing({} as Record<PropertyType, EditingState>);
  };

  const beginEdit = (
    propertyType: PropertyType,
    existing?: PropertySurcharge
  ) => {
    setEditing((prev) => ({
      ...prev,
      [propertyType]: {
        surchargeCents: existing?.surchargeCents ?? 0,
        active: existing?.active ?? true,
      },
    }));
  };

  const cancelEdit = (propertyType: PropertyType) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[propertyType];
      return next;
    });
  };

  const handleSave = async (propertyType: PropertyType) => {
    const values = editing[propertyType];
    if (!values) return;
    await upsertMutation.mutateAsync({
      propertyType,
      surchargeCents: values.surchargeCents,
      carrierServiceId:
        scope.kind === "carrierService" ? scope.carrierServiceId : null,
      active: values.active,
    });
    cancelEdit(propertyType);
  };

  const updateEditing = (
    propertyType: PropertyType,
    field: keyof EditingState,
    value: number | boolean
  ) => {
    setEditing((prev) => ({
      ...prev,
      [propertyType]: {
        ...(prev[propertyType] ?? { surchargeCents: 0, active: true }),
        [field]: value,
      } as EditingState,
    }));
  };

  if (surchargesLoading || servicesLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const allPropertyTypes = Object.values(PropertyType);
  const showInitDefaults =
    scope.kind === "global" && (!surcharges || surcharges.length === 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Package className="h-5 w-5" />
            Property Type Surcharges
          </h3>
          <Select
            value={
              scope.kind === "global" ? GLOBAL_SCOPE : scope.carrierServiceId
            }
            onValueChange={handleScopeChange}
          >
            <SelectTrigger className="w-[280px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Scope</SelectLabel>
                <SelectItem value={GLOBAL_SCOPE}>
                  Global (all services)
                </SelectItem>
              </SelectGroup>
              {carrierGroups.map(({ carrierName, services }) => (
                <SelectGroup key={carrierName}>
                  <SelectLabel>{carrierName}</SelectLabel>
                  {services.map((cs) => (
                    <SelectItem key={cs.id} value={cs.id}>
                      {cs.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>
        {showInitDefaults && (
          <Button
            onClick={() => initializeMutation.mutate()}
            disabled={initializeMutation.isPending}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Initialize Defaults
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property Type</TableHead>
                <TableHead>Surcharge</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allPropertyTypes.map((pt) => {
                const surcharge = surchargeByPropertyType[pt];
                const isEditing = pt in editing;
                return (
                  <TableRow key={pt}>
                    <TableCell className="font-medium">
                      {getPropertyTypeLabel(pt)}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <div className="flex items-center gap-1 max-w-[150px]">
                          <span className="text-sm">$</span>
                          <Input
                            type="number"
                            value={(editing[pt].surchargeCents ?? 0) / 100}
                            onChange={(e) =>
                              updateEditing(
                                pt,
                                "surchargeCents",
                                Math.round(
                                  parseFloat(e.target.value || "0") * 100
                                )
                              )
                            }
                            step="0.01"
                            min="0"
                            className="h-8"
                          />
                        </div>
                      ) : (
                        <span className={surcharge ? "" : "text-gray-400"}>
                          {surcharge
                            ? formatCurrency(surcharge.surchargeCents)
                            : "$0.00"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Switch
                          checked={editing[pt].active}
                          onCheckedChange={(checked) =>
                            updateEditing(pt, "active", checked)
                          }
                        />
                      ) : (
                        <Switch
                          checked={surcharge?.active ?? false}
                          onCheckedChange={(checked) => {
                            void upsertMutation.mutateAsync({
                              propertyType: pt,
                              surchargeCents: surcharge?.surchargeCents ?? 0,
                              carrierServiceId:
                                scope.kind === "carrierService"
                                  ? scope.carrierServiceId
                                  : null,
                              active: checked,
                            });
                          }}
                          disabled={upsertMutation.isPending}
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => void handleSave(pt)}
                            disabled={upsertMutation.isPending}
                            size="sm"
                            variant="ghost"
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => cancelEdit(pt)}
                            size="sm"
                            variant="ghost"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => beginEdit(pt, surcharge)}
                          size="sm"
                          variant="ghost"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
